import { Plugin } from 'chart.js'
import { DateTime } from 'luxon'
import { SeriesGroup } from '../../utils/series'

const BAND_COLOR = 'rgba(255, 255, 255, 0.04)'

/**
 * Draw alternating background bands one wall-clock hour wide, walking the
 * `groups` (15-min buckets) and shading every other hour. Shared by all
 * charts that visualize quarter-scale data.
 */
export function quarterHourBandsPlugin(groups: SeriesGroup[]): Plugin {
  return {
    id: 'hourBands',
    beforeDraw: (chart) => {
      const { ctx, chartArea, scales } = chart
      if (!chartArea || groups.length === 0) return
      const xScale = scales.x
      const stepPx = xScale.getPixelForValue(1) - xScale.getPixelForValue(0)

      const drawBand = (fromIdx: number, toIdx: number) => {
        // getPixelForValue returns pixels outside chartArea for off-screen
        // indices, which would paint over Y-axis titles and beyond — clamp.
        const x1 = Math.max(chartArea.left, xScale.getPixelForValue(fromIdx) - stepPx / 2)
        const x2 = Math.min(chartArea.right, xScale.getPixelForValue(toIdx) + stepPx / 2)
        if (x2 <= x1) return
        ctx.save()
        ctx.fillStyle = BAND_COLOR
        ctx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top)
        ctx.restore()
      }

      let currentHour = -1
      let hourIndex = 0
      let bandStart = -1

      for (let i = 0; i < groups.length; i++) {
        const dt = DateTime.fromMillis(groups[i].from).toLocal()
        if (dt.hour !== currentHour) {
          if (hourIndex % 2 === 1 && bandStart >= 0) {
            drawBand(bandStart, i - 1)
          }
          currentHour = dt.hour
          hourIndex++
          bandStart = i
        }
      }
      if (hourIndex % 2 === 1 && bandStart >= 0) {
        drawBand(bandStart, groups.length - 1)
      }
    }
  }
}

/**
 * Replace the per-bucket labels with hour numbers at :00 boundaries and
 * blanks elsewhere — pairs with `quarterHourBandsPlugin` for a tidier
 * 15-min x-axis. `labels.length` may exceed `groups.length` because of
 * panX right-padding; those trailing labels stay blank.
 */
export function quarterHourLabels(labels: string[], groups: SeriesGroup[]): string[] {
  const out: string[] = new Array(labels.length).fill('')
  for (let i = 0; i < groups.length && i < labels.length; i++) {
    const dt = DateTime.fromMillis(groups[i].from).toLocal()
    if (dt.minute === 0) {
      out[i] = String(dt.hour)
    }
  }
  return out
}
